import { FC, useState, useCallback, useMemo, useRef } from "react";
import { Box, IconButton, Chip, Button, Alert, FormControl, Select, MenuItem, Stack, Typography, TextField, Snackbar, Alert as MuiAlert, TablePagination, useMediaQuery, useTheme } from "@mui/material";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TableList, { TableColumn } from '../../../components/common/TableList';
import { ItemsModal, CustomAction } from '@/components/common/ItemsModal';
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { Document } from "@/types/document.types";
import { selectPaginatedDocuments, selectDocumentsPagination, selectDocumentsTotalCount, selectDocumentsLoading, selectDocumentsError, selectUploadLoading, selectUploadError } from "@/features/documents/documentsSelectors";
import type { DocumentVisibleUnit } from "@/features/documents/documentsSelectors";
import { setDocumentsPage, setDocumentsPageSize } from "@/features/documents/documentsSlice";
import { fetchDocumentsWithFilters, uploadDocument, deleteDocument, deleteFolder } from "@/features/documents/documentsThunks";
import { downloadFile } from "@/utils/downloadFile";
import { fetchDocumentUrl } from "@/features/documents/documentsThunks";
import { validateFile } from "../validations";
import { formatFileName, getFileExtension } from "../docHelper";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useSnackbar } from "@/hooks/useSnackbar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useAuth } from "@/hooks/useAuth";
import { canUploadDocuments } from "@/utils/permissions";
import FolderUploadModal from "./FolderUploadModal";

type DocumentRow = Document & {
    _isFolder?: boolean;
    _folderKey?: string;
    _folderName?: string;
    _fileCount?: number;
    _folderDocuments?: Document[];
    _isChild?: boolean;
};

const DocTable: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const paginatedUnits: DocumentVisibleUnit[] = useSelector(selectPaginatedDocuments);
    const { currentPage, pageSize } = useSelector(selectDocumentsPagination);
    const totalCount = useSelector(selectDocumentsTotalCount);
    const loadingDocs = useSelector(selectDocumentsLoading);
    const errorDocs = useSelector(selectDocumentsError);
    const uploading = useSelector(selectUploadLoading);
    const uploadError = useSelector(selectUploadError);
    const { userRole, authUser} = useAuth();
    const snackbar = useSnackbar();
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const tableRef = useRef<HTMLDivElement>(null);

    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState('');
    const [documentName, setDocumentName] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [folderDeleteDialogOpen, setFolderDeleteDialogOpen] = useState(false);
    const [folderToDelete, setFolderToDelete] = useState<{ folderName: string; documents: Document[] } | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

    const toggleFolder = useCallback((folderKey: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderKey)) next.delete(folderKey);
            else next.add(folderKey);
            return next;
        });
    }, []);

    const handlePageChange = useCallback((_event: unknown, newPage: number) => {
        dispatch(setDocumentsPage(newPage + 1));
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [dispatch]);

    const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setDocumentsPageSize(parseInt(event.target.value, 10)));
    }, [dispatch]);

    const displayRows: DocumentRow[] = useMemo(() => {
        const rows: DocumentRow[] = [];

        for (const unit of paginatedUnits) {
            if (unit.kind === 'folder') {
                const folder = unit.folder;
                rows.push({
                    id: `folder-${folder.folderKey}`,
                    url: '',
                    name: folder.folderName,
                    tag: folder.tag,
                    fileSize: '',
                    lastEdition: '',
                    lastEditor: folder.documents[0]?.lastEditor || '',
                    _isFolder: true,
                    _folderKey: folder.folderKey,
                    _folderName: folder.folderName,
                    _fileCount: folder.fileCount,
                    _folderDocuments: folder.documents,
                });

                if (expandedFolders.has(folder.folderKey)) {
                    for (const doc of folder.documents) {
                        rows.push({ ...doc, _isChild: true });
                    }
                }
            } else {
                rows.push(unit.document);
            }
        }

        return rows;
    }, [paginatedUnits, expandedFolders]);

    const handleDownload = async (doc: Document) => {
        try {
            const downloadUrl = await dispatch(fetchDocumentUrl({ storagePath: doc.url })).unwrap();

            await downloadFile(downloadUrl, doc.url || 'documento');
        } catch (err) {
            console.error('Error downloading document:', err);
            alert('Error al descargar el documento. Por favor, intente nuevamente.');
        }
    };

    const handleUpload = () => {
        console.log(userRole);   
        ;
        setUploadModalOpen(true);
    };

    const handleCloseUploadModal = () => {
        setUploadModalOpen(false);
        setSelectedTag('');
        setDocumentName('');
        setSelectedFile(null);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleConfirmUpload = async () => {
        if (!selectedFile || !selectedTag) {
            return;
        }

        const validation = validateFile(selectedFile);
        if (!validation.valid) {
            return;
        }

        try {
            const fileName = documentName.trim() || selectedFile.name;
            const extension = getFileExtension(selectedFile.name);
            // URL stores only the tag folder path (without 'documents/' prefix)
            const documentUrl = `${selectedTag}/${formatFileName(fileName)}${extension}`;

            await dispatch(uploadDocument({
                file: selectedFile,
                documentName: fileName,
                tag: selectedTag,
                documentUrl
            })).unwrap();

            // Refresh documents list with current filters (empty in this case)
            dispatch(fetchDocumentsWithFilters({}));
            handleCloseUploadModal();
        } catch (err) {
            console.error('Error uploading document:', err);
        }
    };

    const handleDeleteClick = useCallback((doc: Document) => {
        if (!doc.id) {
            snackbar.show('Error: ID de documento no válido', 'error');
            return;
        }
        setDocumentToDelete(doc);
        setDeleteDialogOpen(true);
    }, [snackbar]);

    const cancelDelete = useCallback(() => {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!documentToDelete?.id) {
            setDeleteDialogOpen(false);
            return;
        }

        setIsDeleting(true);
        try {
            await dispatch(deleteDocument({
                documentId: documentToDelete.id,
                storagePath: documentToDelete.url
            })).unwrap();

            snackbar.show('Documento eliminado correctamente', 'success');
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
        } catch (error: any) {
            snackbar.show(error || 'Error al eliminar el documento', 'error');
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    }, [dispatch, snackbar, documentToDelete]);

    const handleFolderDeleteClick = useCallback((d: DocumentRow) => {
        if (!d._folderDocuments?.length) return;
        setFolderToDelete({ folderName: d._folderName!, documents: d._folderDocuments });
        setFolderDeleteDialogOpen(true);
    }, []);

    const cancelFolderDelete = useCallback(() => {
        setFolderDeleteDialogOpen(false);
        setFolderToDelete(null);
    }, []);

    const confirmFolderDelete = useCallback(async () => {
        if (!folderToDelete) {
            setFolderDeleteDialogOpen(false);
            return;
        }

        setIsDeleting(true);
        try {
            await dispatch(deleteFolder({
                documents: folderToDelete.documents,
                folderName: folderToDelete.folderName,
            })).unwrap();

            snackbar.show(`Carpeta "${folderToDelete.folderName}" eliminada correctamente`, 'success');
            setFolderDeleteDialogOpen(false);
            setFolderToDelete(null);
            setExpandedFolders(prev => {
                const next = new Set(prev);
                next.forEach(key => {
                    if (key.endsWith(`/${folderToDelete.folderName}`)) next.delete(key);
                });
                return next;
            });
        } catch (error: any) {
            snackbar.show(error || 'Error al eliminar la carpeta', 'error');
            setFolderDeleteDialogOpen(false);
            setFolderToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    }, [dispatch, snackbar, folderToDelete]);

    // // Custom actions for upload modal
    const uploadActions: CustomAction[] = [
        {
            label: 'Cancelar',
            onClick: handleCloseUploadModal,
            variant: 'outlined',
            disabled: uploading
        },
        {
            label: 'Subir',
            onClick: handleConfirmUpload,
            variant: 'contained',
            disabled: !selectedFile || !selectedTag || uploading,
            loading: uploading
        }
    ];

    const columns: TableColumn<DocumentRow>[] = [
        {
            key: 'name', label: 'Título', width: 360, headerSx: { color: '#374151' },
            render: (d) => {
                if (d._isFolder) {
                    const isExpanded = expandedFolders.has(d._folderKey!);
                    return (
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            onClick={() => toggleFolder(d._folderKey!)}
                        >
                            <ChevronRightIcon
                                sx={{
                                    fontSize: 20,
                                    color: '#6b7280',
                                    transition: 'transform 0.2s',
                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            />
                            <FolderOutlinedIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                            <Typography variant="body2" fontWeight={600} sx={{ ml: 0.5 }}>
                                {d._folderName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({d._fileCount} archivo{d._fileCount !== 1 ? 's' : ''})
                            </Typography>
                        </Box>
                    );
                }
                return (
                    <Box sx={{ pl: d._isChild ? 5 : 0 }}>
                        <Typography variant="body2">{d.name}</Typography>
                    </Box>
                );
            }
        },
        {
            key: 'tag', label: 'Etiqueta', render: (d) => (
                <Chip label={d.tag} size="small" sx={{ backgroundColor: '#d1f4eb', color: '#00695c', fontWeight: 500 }} />
            ), headerSx: { color: '#374151' }
        },
        {
            key: 'fileSize', label: 'Tamaño', align: 'left', headerSx: { color: '#374151' },
            render: (d) => d._isFolder ? null : (d.fileSize || '-')
        },
        {
            key: 'lastEdition', label: 'Última Edición', headerSx: { color: '#374151' },
            render: (d) => d._isFolder ? null : (d.lastEdition || '-')
        },
        {
            key: 'lastEditor', label: 'Subido por', headerSx: { color: '#374151' },
            render: (d) => d.lastEditor || '-'
        },
        {
            key: 'actions', label: 'Acciones', align: 'center', width: 120, render: (d) => {
                if (d._isFolder) {
                    const currentUserName = authUser?.displayName || authUser?.email || '';
                    const allOwnedByUser = d._folderDocuments?.every(doc => doc.lastEditor === currentUserName);
                    if (!allOwnedByUser) return null;
                    return (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton
                                size="small"
                                onClick={() => handleFolderDeleteClick(d)}
                                sx={{ color: '#6b7280', '&:hover': { color: 'error.main' } }}
                                disabled={isDeleting}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                }

                const currentUserName = authUser?.displayName || authUser?.email || '';
                const isLastEditor = d.lastEditor === currentUserName;

                return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={() => handleDownload(d)}
                            sx={{ color: '#6b7280', '&:hover': { color: 'primary.main' } }}
                        >
                            <FileDownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                        {isLastEditor && (
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(d)}
                                sx={{ color: '#6b7280', '&:hover': { color: 'error.main' } }}
                                disabled={isDeleting}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                );
            },
            cellSx: { px: 1 }
        }
    ];

    const canUpload = canUploadDocuments(userRole, authUser?.email);

    const uploadButtons = canUpload ? (
        <Stack direction="row" spacing={2}>
            <Button
                variant="contained"
                startIcon={<InsertDriveFileOutlinedIcon />}
                onClick={handleUpload}
                sx={{ textTransform: 'none', px: 3, backgroundColor: 'primary.dark', color: 'white', '&:hover': { backgroundColor: '#111827' } }}
            >
                Subir Documento
            </Button>
            <Button
                variant="outlined"
                startIcon={<CreateNewFolderOutlinedIcon />}
                onClick={() => setFolderModalOpen(true)}
                sx={{ textTransform: 'none', px: 3, borderColor: 'primary.dark', color: 'primary.dark', '&:hover': { backgroundColor: 'rgba(17, 24, 39, 0.04)' } }}
            >
                Subir Carpeta
            </Button>
        </Stack>
    ) : null;

    const paginationControl = totalCount > 0 ? (
        <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage={isXs ? '' : 'Filas por página:'}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            sx={{
                border: 'none',
                '.MuiTablePagination-actions .Mui-disabled': { opacity: 0.38 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '.MuiButtonBase-root.Mui-selected': { color: 'primary.main' },
            }}
        />
    ) : null;

    const footer = (uploadButtons || paginationControl) ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 2 }}>
            {uploadButtons ?? <Box />}
            {paginationControl}
        </Box>
    ) : undefined;

    return (
        <>
            {loadingDocs && <LoadingOverlay message="Cargando documentos..." />}
            {isDeleting && <LoadingOverlay message="Eliminando documento, por favor espere..." />}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={snackbar.close}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>

            <ConfirmDialog
                open={deleteDialogOpen}
                title="Confirmar eliminacion"
                message="Estas seguro de que quieres eliminar el documento"
                itemName={documentToDelete?.name}
                warningMessage="Esta accion no se puede deshacer. El documento y su archivo asociado seran eliminados permanentemente."
                confirmText="Eliminar"
                cancelText="Cancelar"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                confirmColor="error"
                isLoading={isDeleting}
            />

            <ConfirmDialog
                open={folderDeleteDialogOpen}
                title="Eliminar carpeta completa"
                message={`Estas seguro de que quieres eliminar la carpeta con todos sus archivos (${folderToDelete?.documents.length || 0})`}
                itemName={folderToDelete?.folderName}
                warningMessage="Esta accion no se puede deshacer. Todos los archivos dentro de la carpeta seran eliminados permanentemente."
                confirmText="Eliminar todo"
                cancelText="Cancelar"
                onConfirm={confirmFolderDelete}
                onCancel={cancelFolderDelete}
                confirmColor="error"
                isLoading={isDeleting}
            />

            <Box ref={tableRef} sx={{ py: 6, px: { xs: 4, md: 6, lg: 8 } }}>
                {errorDocs ? (
                    <Alert severity="error">{errorDocs}</Alert>
                ) : (
                    <Box
                        key={`docs-page-${currentPage}-${pageSize}`}
                        sx={{
                            animation: 'docsTableFadeIn 150ms ease',
                            '@keyframes docsTableFadeIn': {
                                from: { opacity: 0 },
                                to: { opacity: 1 },
                            },
                        }}
                    >
                        <TableList<DocumentRow>
                            data={displayRows}
                            columns={columns}
                            title="Documentos"
                            getRowKey={(d) => d._isFolder ? d._folderKey! : (d.id || '')}
                            containerSx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}
                            tableSx={{}}
                            footer={footer}
                            headerBackground={true}
                            emptyMessage="No hay documentos registrados"
                        />
                    </Box>
                )}
            </Box>

            {/* Upload Modal using ItemsModal */}
            {canUploadDocuments(userRole, authUser?.email) ?
                <ItemsModal
                    open={uploadModalOpen}
                    onClose={handleCloseUploadModal}
                    title="Subir Nuevo Documento"
                    showCustomActions={true}
                    customActions={uploadActions}
                    isLoading={uploading}
                >
                    <Stack spacing={3}>
                        {uploadError && <Alert severity="error">{uploadError}</Alert>}
                        <FormControl fullWidth>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                Nombre del Documento
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Ingrese el nombre del documento"
                                value={documentName}
                                onChange={(e) => setDocumentName(e.target.value)}
                                disabled={uploading}
                                variant="outlined"
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                Etiqueta
                            </Typography>
                            <Select
                                value={selectedTag}
                                onChange={(e) => setSelectedTag(e.target.value)}
                                disabled={uploading}
                            >
                                <MenuItem value="">Selecciona una etiqueta</MenuItem>
                                <MenuItem key={'academico'} value="Académico">Académico</MenuItem>
                                <MenuItem key={'administrativo'} value="Administrativo">Administrativo</MenuItem>
                                <MenuItem key={'general'} value="General">General</MenuItem>
                            </Select>
                        </FormControl>
                        <Box>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                Archivo
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUploadOutlinedIcon />}
                                sx={{ textTransform: 'none', py: 2 }}
                                disabled={uploading}
                            >
                                {selectedFile ? selectedFile.name : 'Selecciona un archivo'}
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileSelect}
                                    disabled={uploading}
                                />
                            </Button>
                        </Box>
                    </Stack>
                </ItemsModal>
            : null}

            {/* Folder Upload Modal */}
            {canUploadDocuments(userRole, authUser?.email) && (
                <FolderUploadModal
                    open={folderModalOpen}
                    onClose={() => {
                        setFolderModalOpen(false);
                        dispatch(fetchDocumentsWithFilters({}));
                    }}
                />
            )}
        </>
    );
};

export default DocTable;